import React from 'react';
import * as S from './AdvancesStyles';

const fmt = n => (n < 0 ? '−₹' : '₹') + Math.round(Math.abs(n)).toLocaleString('en-IN');
const fmtDate = d => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const PassbookDrawer = ({
  show,
  memberId,
  members,
  txns,
  memberStats,
  apiary,
  locLabel,
  isAdmin = true,
  onClose,
  onOpenAddEntry,
  onOpenSettle,
  onPayAllForMember
}) => {
  if (!memberId) return null;

  const m = members.find(x => x.id === memberId);
  if (!m) return null;

  const s = memberStats(memberId);

  // Filter transactions for this member and sort chronological ascending to compute running balance
  const memberTxns = txns
    .filter(t => String(t.memberId) === String(memberId))
    .sort((a, b) => a.date.localeCompare(b.date));

  let running = 0;
  const passbookItems = memberTxns.map(t => {
    let sign = '',
      amtClass = '',
      desc = '',
      meta = '';

    if (t.type === 'advance_issued') {
      sign = '+';
      amtClass = 'amt-pos';
      running += t.amount;
      desc = 'Advance issued';
      meta = t.locId ? 'Linked: ' + (apiary(t.locId)?.name || t.locId) : 'Independent';
    } else if (t.type === 'advance_settled') {
      sign = '−';
      amtClass = 'amt-neg';
      running -= t.amount;
      desc = 'Advance settled/adjusted';
      meta = t.comment || '';
    } else if (t.type === 'expense') {
      desc = t.category + ' expense · ' + locLabel(t);
      meta = t.approvalStatus.charAt(0).toUpperCase() + t.approvalStatus.slice(1);
      if (t.approvalStatus === 'approved') {
        sign = '−';
        amtClass = 'amt-neg';
        running -= t.amount;
      } else {
        sign = '';
        amtClass = 'rowmuted';
        meta += ' (not yet affecting balance)';
      }
    } else if (t.type === 'adjustment') {
      desc =
        t.amount < 0
          ? 'Reconciliation — credit / write-off'
          : 'Reconciliation — extra owed';
      meta = t.comment || '';
      sign = '';
      amtClass = t.amount < 0 ? 'amt-pos' : 'amt-owe';
    }

    const runDisplay =
      t.type === 'advance_issued' ||
      t.type === 'advance_settled' ||
      (t.type === 'expense' && t.approvalStatus === 'approved')
        ? `Balance: ${fmt(running)}`
        : '';

    return {
      t,
      sign,
      amtClass,
      desc,
      meta,
      runDisplay
    };
  }).reverse(); // Display latest first

  return (
    <>
      <S.DrawerOverlay show={show} onClick={onClose} />
      <S.DrawerContainer show={show}>
        <div className="drawer-head">
          <button className="close" onClick={onClose}>
            ✕
          </button>
          <div className="drawer-name">{m.name}</div>
          <div className="drawer-role">
            {m.role} · {m.mode === 'advance' ? 'Advance model' : 'Reimbursement model'}
          </div>

          <div className="drawer-balance">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="lbl">
                {m.mode === 'advance'
                  ? 'Current Available Balance (Wallet)'
                  : 'Net Available Wallet Balance'}
              </div>
              <span style={{ fontSize: '10px', background: 'rgba(96, 165, 250, 0.2)', color: '#60A5FA', padding: '2px 8px', borderRadius: '12px', fontWeight: 700 }}>
                Mobile Wallet Sync
              </span>
            </div>
            <div className="val" style={{ color: '#60A5FA', fontSize: '26px', marginTop: '4px' }}>
              {fmt(s.netWalletBalance !== undefined ? s.netWalletBalance : s.balance)}
            </div>

            <div style={{
              marginTop: '12px',
              paddingTop: '10px',
              borderTop: '1px solid rgba(255,255,255,0.12)',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px'
            }}>
              <div>
                <div style={{ color: '#9CA3AF', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase' }}>Approved Balance</div>
                <div style={{ color: '#10B981', fontWeight: 800, fontSize: '14px', marginTop: '2px' }}>
                  {fmt(s.balance)}
                </div>
              </div>
              <div>
                <div style={{ color: '#9CA3AF', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase' }}>Need Approval (Pending)</div>
                <div style={{ color: '#F59E0B', fontWeight: 800, fontSize: '14px', marginTop: '2px' }}>
                  {fmt(s.pendingExpenses || 0)}
                </div>
              </div>
            </div>

            {m.mode === 'advance' ? (
              s.overdrawn && (
                <div className="flag" style={{ marginTop: '10px' }}>
                  Overdrawn — {fmt(s.converted)} converted to reimbursement payable
                </div>
              )
            ) : (
              <div className="flag" style={{ marginTop: '10px' }}>
                {s.paid > 0 ? `${fmt(s.paid)} already paid out.` : ''}
                {s.credit > 0
                  ? ` ${fmt(s.credit)} credit on file — will offset next approved expense.`
                  : ''}
              </div>
            )}
          </div>

          {isAdmin ? (
            <div className="drawer-actions">
              {m.mode === 'advance' ? (
                <>
                  <S.Btn
                    sm
                    onClick={() => {
                      onClose();
                      onOpenAddEntry('advance_issued', memberId);
                    }}
                  >
                    Issue Advance
                  </S.Btn>
                  <S.Btn ghost sm onClick={() => onOpenSettle(memberId)}>
                    Settle / Reset Ledger
                  </S.Btn>
                </>
              ) : (
                <>
                  {s.unpaid > 0 && (
                    <S.Btn sm onClick={() => onPayAllForMember(memberId)}>
                      Mark Full Balance Paid
                    </S.Btn>
                  )}
                  <S.Btn ghost sm onClick={() => onOpenSettle(memberId)}>
                    Settle Lump Sum…
                  </S.Btn>
                </>
              )}
            </div>
          ) : (
            <div style={{ marginTop: '14px', fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic', textAlign: 'center' }}>
              <i className="fa fa-lock mr-1"></i> Passbook statement (View-Only)
            </div>
          )}
        </div>

        <div className="drawer-body">
          {passbookItems.length ? (
            passbookItems.map(item => (
              <div
                key={item.t.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: '1px solid #E7E8EC',
                  fontSize: '12.5px'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#6B7280', fontSize: '11px' }}>
                    {fmtDate(item.t.date)}
                  </div>
                  <div style={{ fontWeight: 700, marginTop: '1px' }}>
                    {item.desc}
                  </div>
                  <div style={{ color: '#6B7280', fontSize: '11px', marginTop: '2px' }}>
                    {item.meta}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flex: 'none', paddingLeft: '10px' }}>
                  <div
                    style={{
                      fontWeight: 800,
                      color:
                        item.amtClass === 'amt-pos'
                          ? '#1E9E5A'
                          : item.amtClass === 'amt-neg'
                          ? '#D5372A'
                          : item.amtClass === 'amt-owe'
                          ? '#B9791A'
                          : '#9297A3'
                    }}
                  >
                    {item.sign}
                    {fmt(item.t.amount)}
                  </div>
                  <div style={{ fontSize: '10.5px', color: '#6B7280', marginTop: '2px' }}>
                    {item.runDisplay}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
              No activity yet.
            </div>
          )}
        </div>
      </S.DrawerContainer>
    </>
  );
};

export default PassbookDrawer;
